import { useGlobalStore } from "@/store/GlobalStore";
import { ExclamationCircleFilled } from "@ant-design/icons";
import { Button, Checkbox, Input, Modal, Radio, Textarea } from "@renderbus/ui";
import React from "react";
import { Steps } from "../../../../components/renderbus-steps";
import {
  ErrorWrapper,
  SignOutConfirmWrapper,
  SignOutModalContent,
  SignOutModalHeader,
  SignOutModalInfo,
  SignOutModalRemark,
  SignOutReasonWrapper,
} from "./atom";
import { useTranslation } from "react-i18next";
import { API } from "./api";
import CountdownButton from "./components/countdown-button";
import { message } from "antd";
import { logoutHelperCommon } from "@/utils/logoutHelper";
import { STORAGE } from "project-config/constants";
import { Cookie } from "project-config/utils";
type SignOutModalProps = {
  open: boolean;
  onCancel?: () => void;
  onOk?: () => void;
};
const maskPhone = (phone?: string) =>
  !phone
    ? ""
    : phone
        .split("")
        .map((item: string, index: number) =>
          index < 3 || index > 6 ? item : "*"
        )
        .join("");

const SignOutModal: React.FC<SignOutModalProps> = ({
  open,
  onCancel,
  onOk,
}: SignOutModalProps) => {
  const { t } = useTranslation("profile");

  const reasonGroup = [
    t("user_sign_out_reason_account_change"),
    t("user_sign_out_reason_safety"),
    t("user_sign_out_reason_hard"),
    t("user_sign_out_reason_other"),
  ];

  const { userInfo, isDomestic } = useGlobalStore();
  const [current, setCurrent] = React.useState(0);
  const [agree, setAgree] = React.useState(false);
  const [disabled, setDisabled] = React.useState(false);
  const [validateCode, setValidateCode] = React.useState("");
  const [reasonOrder, setReasonOrder] = React.useState(0);
  const [detailReason, setDetailReason] = React.useState(reasonGroup[0]);
  const [validateFail, setValidateFail] = React.useState(false);
  const [otherReason, setOtherReason] = React.useState("");
  const stepItems = [
    {
      title: t("user_sign_out_notice"),
    },
    {
      title: t("user_sign_out_validate"),
    },
    {
      title: t("user_sign_out_reason"),
    },
  ];

  // 获取验证码
  const getValidateCode = async (
    phone?: string,
    email?: string,
    userName?: string
  ) => {
    isDomestic
      ? await API.sendPhoneCode(phone)
      : await API.sendEmailCode(email, userName);
  };
  const onModalCancel = () => {
    onCancel?.();
    setCurrent(0);
    setAgree(false);
    setValidateCode("");
    setReasonOrder(0);
    setDetailReason(reasonGroup[0]);
  };
  const onConfirm = async () => {
    if (current < 2) {
      if (current === 1) {
        const { code } = isDomestic
          ? await API.checkDeleteCode(userInfo?.phone, validateCode)
          : await API.validateCode(userInfo?.email, validateCode);
        setValidateFail(code !== 200);
        if (code !== 200) {
          return;
        }
      }
      setCurrent(current + 1);
    } else {
      if (reasonOrder === 3 && !otherReason) return;
      const { code } = await API.deleteUser(
        reasonOrder === 3 ? otherReason : detailReason
      );
      if (code === 200) {
        message.success(t("user_sign_out_tip_success"));
        logoutHelperCommon();
      }
      onModalCancel();
    }
  };
  React.useEffect(() => {
    if (current === 0) {
      setDisabled(!agree);
    }
    if (current === 1) {
      setDisabled(!validateCode);
    }
    if (current === 2) {
      setDisabled(reasonOrder === 3 && !otherReason);
    }
  }, [current, agree, validateCode, otherReason, reasonOrder]);
  return (
    <Modal
      width={Cookie.getCookie(STORAGE.LANGUAGE_FLAG) === "1" ? 900 : 780}
      open={open}
      onCancel={onModalCancel}
      footer={
        <>
          <Button onClick={onModalCancel}>
            {t("user_sign_out_btn_cancel")}
          </Button>
          <Button type="primary" disabled={disabled} onClick={onConfirm}>
            {current < 2
              ? t("user_sign_out_btn_next")
              : t("user_sign_out_btn_confirm")}
          </Button>
        </>
      }
      title={
        <>
          <span>{t("user_sign_out_title")}</span>
          <SignOutModalHeader>
            <ExclamationCircleFilled />
            {t("user_sign_out_modal_title_extra")}
          </SignOutModalHeader>
        </>
      }
    >
      <SignOutModalContent>
        <Steps items={stepItems} current={current}></Steps>
        {current < 2 && (
          <SignOutModalInfo>
            <div className="title">{t("user_sign_out_content_title")}</div>
            <div className="username">{userInfo?.userName}</div>
          </SignOutModalInfo>
        )}
        {current === 0 && (
          <>
            <SignOutModalRemark>
              <p>
                {t("user_sign_out_content_p0", {
                  val: isDomestic ? "Renderbus" : "Fox Renderfarm",
                })}
              </p>
              <p>{t("user_sign_out_content_p2")}</p>
              <p>{t("user_sign_out_content_p3")}</p>
              <p>{t("user_sign_out_content_p4")}</p>
            </SignOutModalRemark>
            <SignOutConfirmWrapper>
              <Checkbox
                checked={agree}
                onChange={(e) => setAgree(e.target.checked)}
              />
              <span className="confirm-text">{t("user_sign_out_agree")}</span>
            </SignOutConfirmWrapper>
          </>
        )}
        {current === 1 && (
          <>
            <SignOutModalInfo className="current-2">
              <div className="tip">
                {isDomestic
                  ? t("user_sign_out_validate_phone")
                  : t("user_sign_out_validate_email")}
              </div>
              <div className="phone">
                {isDomestic ? maskPhone(userInfo?.phone) : userInfo?.email}
              </div>
              <div className="input-field">
                <Input
                  onInput={(e) => setValidateCode(e.currentTarget.value)}
                  onBlur={() => setValidateFail(false)}
                  className="background-191D1F"
                  placeholder={t("user_sign_out_placeholder_validate_code")}
                />
                <CountdownButton
                  onClick={() =>
                    getValidateCode(
                      userInfo?.phone,
                      userInfo?.email,
                      userInfo?.userName
                    )
                  }
                />
              </div>
              <ErrorWrapper>
                {validateFail ? t("user_sign_out_error_validate_code") : ""}
              </ErrorWrapper>
            </SignOutModalInfo>
          </>
        )}
        {current === 2 && (
          <SignOutReasonWrapper
            className={reasonGroup[3].length < 3 ? "" : "abroad-style"}
          >
            <Radio.Group
              value={reasonOrder}
              onChange={(e) => {
                setReasonOrder(e.target.value);
                e.target.value !== 3 &&
                  setDetailReason(reasonGroup[e.target.value]);
              }}
            >
              <div>
                <Radio value={0}>{reasonGroup[0]}</Radio>
                <Radio value={2}>{reasonGroup[2]}</Radio>
              </div>
              <div>
                <Radio value={1}>{reasonGroup[1]}</Radio>
                <Radio value={3}>{reasonGroup[3]}</Radio>
              </div>
            </Radio.Group>
            {reasonOrder === 3 && (
              <Textarea
                value={otherReason}
                className="background-191D1F"
                placeholder={t("user_sign_out_placeholder_reason")}
                maxLength={100}
                onChange={(e) => setOtherReason(e.target.value)}
              />
            )}
          </SignOutReasonWrapper>
        )}
      </SignOutModalContent>
    </Modal>
  );
};
export default SignOutModal;
